import api from '@/lib/api';

export interface RunnerCacheUsage {
  runnerId: string;
  name: string;
  state: string;
  /** Only in `CACHE_RECREATE_REQUIRED`: false for unknown runners, which are not recreated. */
  recreate?: boolean;
}

/** The secret key is write-only and never returned. */
export interface RunnerCache {
  id: string;
  name: string;
  type: string;
  /** host[:port] without scheme. */
  serverAddress: string;
  /** Plain HTTP instead of HTTPS. */
  insecure: boolean;
  bucketName: string;
  bucketLocation: string;
  pathPrefix: string | null;
  shared: boolean;
  /** null lets GitLab Runner detect the addressing style. */
  pathStyle: boolean | null;
  accessKeyId: string;
  created: string;
  usedBy: RunnerCacheUsage[];
}

export interface RunnerCacheConnection {
  serverAddress: string;
  insecure: boolean;
  bucketName: string;
  bucketLocation: string;
  pathPrefix?: string | null;
  shared: boolean;
  pathStyle?: boolean | null;
  accessKeyId: string;
  /** Required on create; omit on update to keep the stored secret. */
  secretKey?: string;
}

export interface RunnerCacheRequest extends RunnerCacheConnection {
  name: string;
}

export interface RunnerCacheUpdateResponse {
  cache: RunnerCache;
  /** Active runners whose VMs are being recreated. */
  recreatedRunners: RunnerCacheUsage[];
}

export type CacheTestCode =
  | 'OK'
  | 'INSECURE_ENDPOINT'
  | 'ADDRESS_NOT_ALLOWED'
  | 'UNREACHABLE'
  | 'TLS_ERROR'
  | 'AUTHENTICATION_FAILED'
  | 'BUCKET_NOT_FOUND'
  | 'ACCESS_DENIED'
  | 'UNEXPECTED_RESPONSE';

export interface CacheTestResult {
  success: boolean;
  code: CacheTestCode;
  step: 'PUT' | 'GET' | 'DELETE' | null;
  message: string;
}

export async function listCaches() {
  const { data } = await api.get<RunnerCache[]>('caches');
  return data;
}

export async function getCache(cacheId: string) {
  const { data } = await api.get<RunnerCache>(`caches/${cacheId}`);
  return data;
}

export async function createCache(payload: RunnerCacheRequest) {
  const { data } = await api.post<RunnerCache>('caches', payload);
  return data;
}

/**
 * Rejects with HTTP 409 `CACHE_RECREATE_REQUIRED` and the affected `runners` when the change would
 * recreate active runners and [recreateRunners] is not set.
 */
export async function updateCache(
  cacheId: string,
  payload: RunnerCacheRequest,
  recreateRunners = false
) {
  const { data } = await api.put<RunnerCacheUpdateResponse>(`caches/${cacheId}`, payload, {
    params: recreateRunners ? { recreateRunners: true } : undefined,
  });
  return data;
}

/** Rejects with HTTP 409 `CACHE_IN_USE` and the blocking `runners` while runners use the cache. */
export async function deleteCache(cacheId: string) {
  await api.delete<void>(`caches/${cacheId}`);
}

export async function testCacheConnection(payload: RunnerCacheConnection) {
  const { data } = await api.post<CacheTestResult>('caches/test', payload);
  return data;
}

/** Tests a saved cache with unsaved edits; an omitted secret uses the stored one. */
export async function testSavedCacheConnection(cacheId: string, payload: RunnerCacheConnection) {
  const { data } = await api.post<CacheTestResult>(`caches/${cacheId}/test`, payload);
  return data;
}

/** The parts of an axios error with a ProblemDetail body that the cache views read. */
export interface CacheProblem {
  message?: string;
  response?: {
    status?: number;
    data?: { code?: string; detail?: string; field?: string; runners?: RunnerCacheUsage[] };
  };
}

export function asProblem(error: unknown): CacheProblem {
  return (error ?? {}) as CacheProblem;
}

/** The runners listed by a 409 `CACHE_IN_USE`, `CACHE_RECREATE_REQUIRED` or `RUNNER_LOCKED` response, if any. */
export function conflictRunners(error: unknown, code: string): RunnerCacheUsage[] | null {
  const { response } = asProblem(error);
  if (response?.status !== 409 || response.data?.code !== code) return null;
  return Array.isArray(response.data.runners) ? response.data.runners : [];
}
